import {
  findModel,
  findProvider,
  readPinnedPreference,
  writePinnedModel,
  writePinnedProvider,
} from './modelPreference'

export const ROUTER_TRANSFER_SOURCE = 'router.samcarlton.com'
export const FRONTIER_TRANSFER_SOURCE =
  'youmightnotneedfrontier.com'
export const FRONTIER_IMPORT_URL =
  'https://youmightnotneedfrontier.com/'
export const MAX_PREFERENCE_TOKEN_LENGTH = 2048

export type PinnedModelReference = {
  provider: string
  modelId: string
}

export type PinnedProviderReference = {
  provider: string
}

export type PreferenceTransferEnvelope = {
  v: 1
  source: string
  pinnedModel: PinnedModelReference | null
  pinnedProvider: PinnedProviderReference | null
}

export type PreferenceImportCandidate =
  | { status: 'missing' }
  | { status: 'invalid'; message: string }
  | {
      status: 'valid'
      envelope: PreferenceTransferEnvelope
    }

function hasExactKeys(
  value: Record<string, unknown>,
  expectedKeys: string[],
): boolean {
  const actualKeys = Object.keys(value).sort()
  return (
    actualKeys.length === expectedKeys.length &&
    actualKeys.every((key, index) => key === expectedKeys[index])
  )
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return window.btoa(binary)
}

function base64ToBytes(value: string): Uint8Array {
  const binary = window.atob(value)
  return Uint8Array.from(binary, (character) =>
    character.charCodeAt(0),
  )
}

export function encodePreferenceEnvelope(
  envelope: PreferenceTransferEnvelope,
): string {
  const bytes = new TextEncoder().encode(JSON.stringify(envelope))
  return bytesToBase64(bytes)
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replace(/=+$/u, '')
}

function decodeBase64Url(value: string): string {
  if (
    value.length === 0 ||
    value.length > MAX_PREFERENCE_TOKEN_LENGTH ||
    !/^[A-Za-z0-9_-]+$/u.test(value)
  ) {
    throw new Error('Preference data is not valid base64url.')
  }
  const base64 = value.replaceAll('-', '+').replaceAll('_', '/')
  const paddingLength = (4 - (base64.length % 4)) % 4
  const bytes = base64ToBytes(base64 + '='.repeat(paddingLength))
  return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
}

export function validatePreferenceEnvelope(
  value: unknown,
  expectedSource: string,
): PreferenceImportCandidate {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      'pinnedModel',
      'pinnedProvider',
      'source',
      'v',
    ])
  ) {
    return {
      status: 'invalid',
      message: 'The preference data does not match version 1.',
    }
  }
  if (value.v !== 1 || value.source !== expectedSource) {
    return {
      status: 'invalid',
      message: 'The preference source or version is not trusted.',
    }
  }

  const pinnedModel = value.pinnedModel
  const pinnedProvider = value.pinnedProvider
  if (
    (pinnedModel === null) === (pinnedProvider === null)
  ) {
    return {
      status: 'invalid',
      message: 'The transfer must contain exactly one preference.',
    }
  }

  if (pinnedModel !== null) {
    if (
      !isRecord(pinnedModel) ||
      !hasExactKeys(pinnedModel, ['modelId', 'provider']) ||
      typeof pinnedModel.provider !== 'string' ||
      typeof pinnedModel.modelId !== 'string' ||
      !findModel(pinnedModel.modelId, pinnedModel.provider)
    ) {
      return {
        status: 'invalid',
        message: 'The pinned model is not in the current registry.',
      }
    }
  }

  if (pinnedProvider !== null) {
    if (
      !isRecord(pinnedProvider) ||
      !hasExactKeys(pinnedProvider, ['provider']) ||
      typeof pinnedProvider.provider !== 'string' ||
      !findProvider(pinnedProvider.provider)
    ) {
      return {
        status: 'invalid',
        message: 'The preferred provider is not in the current registry.',
      }
    }
  }

  return {
    status: 'valid',
    envelope: value as PreferenceTransferEnvelope,
  }
}

export function decodePreferenceEnvelope(
  encoded: string,
  expectedSource: string,
): PreferenceImportCandidate {
  try {
    return validatePreferenceEnvelope(
      JSON.parse(decodeBase64Url(encoded)),
      expectedSource,
    )
  } catch {
    return {
      status: 'invalid',
      message: 'The preference link is malformed.',
    }
  }
}

export function preferenceEnvelopeForShare():
  | PreferenceTransferEnvelope
  | null {
  const preference = readPinnedPreference()
  if (preference.pinnedModel) {
    return {
      v: 1,
      source: ROUTER_TRANSFER_SOURCE,
      pinnedModel: {
        provider: preference.pinnedModel.provider,
        modelId: preference.pinnedModel.modelId,
      },
      pinnedProvider: null,
    }
  }
  if (preference.pinnedProvider) {
    return {
      v: 1,
      source: ROUTER_TRANSFER_SOURCE,
      pinnedModel: null,
      pinnedProvider: {
        provider: preference.pinnedProvider,
      },
    }
  }
  return null
}

export function frontierImportHref(
  envelope: PreferenceTransferEnvelope,
): string {
  return `${FRONTIER_IMPORT_URL}#prefs=${encodePreferenceEnvelope(
    envelope,
  )}`
}

export function capturePreferenceImportFragment(
  location: Pick<Location, 'hash' | 'pathname' | 'search'>,
  history: Pick<History, 'replaceState'>,
): PreferenceImportCandidate {
  if (location.pathname.replace(/\/+$/u, '') !== '/preferences/import') {
    return { status: 'missing' }
  }

  const fragment = location.hash
  history.replaceState(
    null,
    '',
    `${location.pathname}${location.search}`,
  )
  if (
    fragment.length >
    '#prefs='.length + MAX_PREFERENCE_TOKEN_LENGTH
  ) {
    return {
      status: 'invalid',
      message: 'The preference link is too large.',
    }
  }
  const match = /^#prefs=([A-Za-z0-9_-]+)$/u.exec(fragment)
  if (!match?.[1]) {
    return fragment
      ? {
          status: 'invalid',
          message: 'The preference link is malformed.',
        }
      : { status: 'missing' }
  }
  return decodePreferenceEnvelope(
    match[1],
    FRONTIER_TRANSFER_SOURCE,
  )
}

export function applyImportedPreference(
  envelope: PreferenceTransferEnvelope,
): boolean {
  if (envelope.pinnedModel) {
    const model = findModel(
      envelope.pinnedModel.modelId,
      envelope.pinnedModel.provider,
    )
    return model ? writePinnedModel(model) : false
  }

  const provider = findProvider(
    envelope.pinnedProvider?.provider ?? null,
  )
  return provider ? writePinnedProvider(provider) : false
}
